import { useTranslations } from '@aikoa/i18n/react'
import { View } from 'react-native'
import FeedbackAnswersView from '~/components/feedback/FeedbackAnswersView'
import { FeedbackConsentAuth } from '~/components/feedback/FeedbackConsentAuth'
import {
  feedbackFlowDataSchema,
  feedbackFlowDataToQuestionnaireAnswers,
  type ContactForConsent,
  type FeedbackFlowData,
} from '~/components/feedback/feedbackFlowTypes'
import { PhaseWrapper } from '~/components/flows/phases/PhaseWrapper'
import { ToggleableInterestDisplay } from '~/components/interests/ToggleableInterestDisplay'
import { SectionTitle, ViewTitle } from '~/components/layout/Sections'
import { Body } from '~/components/typography/Content'

export function FeedbackReviewPhase({
  flowData,
  showConsentSheet = false,
  consentContact,
  consentFeedbackId,
  consentNonce,
}: {
  flowData: Partial<FeedbackFlowData>
  showConsentSheet?: boolean
  consentContact?: ContactForConsent | null
  consentFeedbackId?: string | null
  consentNonce?: string | null
}) {
  const { t } = useTranslations('feedback')

  const parsedFlowData = feedbackFlowDataSchema.safeParse(flowData)

  if (!parsedFlowData.success) {
    console.error('Invalid flow data', parsedFlowData.error)

    return (
      <PhaseWrapper contentClassName="gap-0 pt-0">
        <ViewTitle testID="feedback-review-phase" className="mb-4">
          {t(($) => $.review.title)}
        </ViewTitle>
        <View className="mb-1 rounded-lg bg-muted px-6 py-4">
          <Body className="text-pretty text-center">{t(($) => $.errors.invalidAnswers)}</Body>
        </View>
      </PhaseWrapper>
    )
  }

  const feedbackAnswers = feedbackFlowDataToQuestionnaireAnswers(
    parsedFlowData.data.feedbackAnswers
  )

  const interestData = parsedFlowData.data?.propertyInterest
  const interestDescription = parsedFlowData.data?.interestDescription?.description ?? null

  const hasAnyInterest =
    !!interestData || (interestDescription && interestDescription.trim().length > 0)

  return (
    <>
      <PhaseWrapper contentClassName="gap-0">
        <ViewTitle testID="feedback-review-phase" className="mb-4">
          {t(($) => $.review.title)}
        </ViewTitle>
        <Body className="mb-4 text-pretty text-center text-lg/normal">
          {t(($) => $.review.description)}
        </Body>
        <View className="mt-4 gap-4">
          <SectionTitle>{t(($) => $.review.answersTitle)}</SectionTitle>
          <FeedbackAnswersView
            answers={feedbackAnswers}
            answersClassName="rounded-content w-full bg-muted/70 py-2"
            itemClassName="px-6"
          />
          {hasAnyInterest && (
            <>
              <SectionTitle className="mt-6">{t(($) => $.review.interestsTitle)}</SectionTitle>
              <ToggleableInterestDisplay
                interest={{
                  answers: [],
                  filters: interestData?.filters ?? {},
                  locations: interestData?.locations ?? [],
                  description: interestDescription ?? null,
                }}
              />
            </>
          )}
        </View>
      </PhaseWrapper>
      <FeedbackConsentAuth
        isOpen={showConsentSheet}
        contact={consentContact ?? null}
        feedbackId={consentFeedbackId}
        nonce={consentNonce}
      />
    </>
  )
}
